/**
 * FilterBar — Dropdown filters (State, Region, Category, KPI) + Quick Filter Chips.
 *
 * Extracted from school.tsx to keep the orchestrator clean.
 * Renders identically to the original inline JSX.
 */

import React from 'react';
import Box from '@mui/material/Box';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { SelectChangeEvent } from '@mui/material/Select';
import QuickFilterChips, { COMMON_QUICK_FILTERS, QuickFilter } from './QuickFilterChips';
import type { UseFiltersReturn } from '../hooks/useFilters';
import type { UseKPIDataReturn } from '../hooks/useKPIData';

interface FilterBarProps {
  filters: UseFiltersReturn;
  kpiData: UseKPIDataReturn;
}

// ── Shared clear-button style ─────────────────────────────────
const clearBtnSx = {
  backgroundColor: '#e0e0e0',
  borderRadius: '0 4px 4px 0',
  padding: '10px',
  marginLeft: '-1px',
  height: '40px',
  width: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': { backgroundColor: '#d0d0d0' },
};

const selectSx = {
  flexGrow: 1,
  '& .MuiOutlinedInput-root': { borderRadius: '4px 0 0 4px' },
};

export default function FilterBar({ filters, kpiData }: FilterBarProps) {
  const {
    state, region, category, kpi, selectedInfraItems,
    activeQuickFilters, globalFilters,
    setState, setRegion, setCategory, setKpi,
    setSelectedInfraItems, setActiveQuickFilters,
    setGlobalFilters, handleReset,
  } = filters;

  const {
    statesList, statesLoading, statesError,
    regionsList, regionsLoading, regionsError,
    categoryList,
    kpiList, kpiLoading, kpiError,
    isInfra,
    expandedRows, setExpandedRows,
    categoryConfig,
  } = kpiData;

  const multiSelectOptions = categoryConfig.kpi?.staticOptions ?? [];

  console.log('[FilterBar]', {
    category, kpi, isInfra,
    configKey: categoryConfig.categoryKey,
    multiSelectOptionsCount: multiSelectOptions.length,
    selectedInfraItems,
  });

  // ── Handlers ─────────────────────────────────────────────
  const onStateChange = (e: SelectChangeEvent) => setState(e.target.value);
  const onRegionChange = (e: SelectChangeEvent) => setRegion(e.target.value);
  const onCategoryChange = (e: SelectChangeEvent) => {
    console.log('[FilterBar] onCategoryChange', e.target.value);
    setCategory(e.target.value);
  };
  const onKpiChange = (e: SelectChangeEvent) => setKpi(e.target.value);

  const onInfraChange = (e: SelectChangeEvent<string[]>) => {
    const v = e.target.value;
    const selected = typeof v === 'string' ? v.split(',') : v;
    console.log('[FilterBar] onInfraChange', { raw: v, selected });
    setSelectedInfraItems(selected);
    setKpi(selected.length > 0 ? 'infra_multi' : '');
  };


  const onReset = () => {
    handleReset();
    setExpandedRows(new Set());
  };

  // Quick filter handlers
  const onQuickFilterApply = (qf: QuickFilter) => {
    const without = globalFilters.filter(f => !f.id.startsWith('quick-'));
    const next = [...without];
    qf.filters.forEach((fd: any) => {
      next.push({ id: `quick-${qf.id}-${fd.field}`, ...fd });
    });
    setGlobalFilters(next);
    setActiveQuickFilters([qf.id]);
  };

  const onQuickFilterRemove = (id: string) => {
    const qf = COMMON_QUICK_FILTERS.find((f: QuickFilter) => f.id === id);
    if (!qf) return;
    const fields = qf.filters.map((f: any) => f.field);
    setGlobalFilters((prev: any[]) =>
      prev.filter(f => !(fields.includes(f.field) && f.id.startsWith(`quick-${id}-`)))
    );
    setActiveQuickFilters((prev: string[]) => prev.filter(x => x !== id));
  };

  return (
    <div className="w-full mb-6 p-8 bg-white rounded-lg shadow-sm">
      <div className="flex gap-4 items-center">
        {/* State */}
        <div className="flex-1">
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel id="state-select-label">State</InputLabel>
              <Select
                labelId="state-select-label"
                id="state-select"
                value={state}
                label="State"
                onChange={onStateChange}
                disabled={statesLoading}
              >
                {statesList.map(s => (
                  <MenuItem key={s.stateID} value={s.stateName} title={`Schools: ${s.schoolCount}`}>
                    {s.stateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton size="small" onClick={() => setState('')} sx={clearBtnSx}>
              <ClearIcon fontSize="medium" />
            </IconButton>
          </Box>
          {statesError && <p className="text-xs text-red-500 mt-1">{statesError}</p>}
        </div>

        {/* Region */}
        <div className="flex-1">
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel id="region-select-label">Region</InputLabel>
              <Select
                labelId="region-select-label"
                id="region-select"
                value={regionsList.some(r => r.roName === region) ? region : ''}
                label="Region"
                onChange={onRegionChange}
                disabled={regionsLoading}
              >
                {regionsList.map(r => (
                  <MenuItem key={r.roID} value={r.roName}>{r.roName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton size="small" onClick={() => setRegion('')} sx={clearBtnSx}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Box>
          {regionsError && <p className="text-xs text-red-500 mt-1">{regionsError}</p>}
        </div>

        {/* Category */}
        <div className="flex-1">
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={category}
                label="Category"
                onChange={onCategoryChange}
              >
                {categoryList.map(c => (
                  <MenuItem key={String(c.key)} value={String(c.key)}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton size="small" onClick={() => setCategory('')} sx={clearBtnSx}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Box>
        </div>

        {/* KPI / Infrastructure multi-select */}
        <div className="flex-1">
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {isInfra ? (
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel id="infra-select-label">Select Infrastructure</InputLabel>
                <Select
                  labelId="infra-select-label"
                  id="infra-select"
                  multiple
                  value={selectedInfraItems}
                  label="Select Infrastructure"
                  onChange={onInfraChange as any}
                  disabled={!category}
                  input={<OutlinedInput label="Select Infrastructure" />}
                  renderValue={(selected: string[]) => {
                    if (selected.length === 0) return '';
                    if (selected.length <= 2) return selected.map(id => multiSelectOptions.find(o => o.key === id)?.label || id).join(', ');
                    return `${selected.length} items selected`;
                  }}
                  MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
                >
                  {/* Select All / Deselect All */}
                  <MenuItem
                    onClick={e => {
                      e.preventDefault();
                      if (selectedInfraItems.length === multiSelectOptions.length) {
                        setSelectedInfraItems([]);
                        setKpi('');
                      } else {
                        setSelectedInfraItems(multiSelectOptions.map(o => o.key));
                        setKpi('infra_multi');
                      }
                    }}
                    sx={{ borderBottom: '1px solid #e0e0e0', fontWeight: 600 }}
                  >
                    <Checkbox
                      checked={selectedInfraItems.length === multiSelectOptions.length}
                      indeterminate={selectedInfraItems.length > 0 && selectedInfraItems.length < multiSelectOptions.length}
                    />
                    <ListItemText primary={selectedInfraItems.length === multiSelectOptions.length ? 'Deselect All' : 'Select All'} />
                  </MenuItem>
                  {multiSelectOptions.map(k => (
                    <MenuItem key={k.key} value={k.key}>
                      <Checkbox checked={selectedInfraItems.includes(k.key)} />
                      <ListItemText primary={k.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel id="kpi-select-label">KPI</InputLabel>
                <Select
                  labelId="kpi-select-label"
                  id="kpi-select"
                  value={kpiList.some(k => k.key === kpi) ? kpi : ''}
                  label="KPI"
                  onChange={onKpiChange}
                  disabled={!category || kpiLoading}
                >
                  {kpiList.map(k => (
                    <MenuItem key={k.key} value={k.key}>{k.label}</MenuItem>
                  ))}
                </Select>
                {kpiError && <p className="text-xs text-red-500 mt-1">{kpiError}</p>}
              </FormControl>
            )}
            <IconButton size="small" onClick={() => { setKpi(''); setSelectedInfraItems([]); }} sx={clearBtnSx}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Box>
        </div>

        {/* Reset */}
        <div className="flex-shrink-0">
          <Button variant="contained" color="secondary" sx={{ height: '50px', width: '102px' }} onClick={onReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Quick Filter Chips */}
      {category && kpi && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <QuickFilterChips
            filters={COMMON_QUICK_FILTERS}
            onApply={onQuickFilterApply}
            onRemove={onQuickFilterRemove}
            activeFilters={activeQuickFilters}
          />
        </div>
      )}
    </div>
  );
}
